import { Content, ItemIndicator, ItemText, Portal, Root, Trigger, Value, Viewport } from '@radix-ui/react-select';
import clsx from 'clsx';
import type { FunctionComponent, ReactNode } from 'react';

import { IconChevronGrabberVertical, IconPlus } from '../../core/icons';
import { CheckIcon } from '../../core/icons/CheckIcon';
import { StyledItem } from '../../core/select/Select';

type TriggerSize = 'l' | 'm';

export type JobArchitectureStatistics = {
    readonly approvedLevels?: number;
    readonly approvedRoles?: number;
    readonly clustersCount?: number;
    readonly expected: number;
    readonly familiesCount?: number;
    readonly levelsCount?: number;
    readonly profiles: number;
    readonly publishedLevels?: number;
    readonly publishedRoles?: number;
    readonly reviewed: number;
    readonly reviewerLevels?: number;
    readonly reviewers: number;
    readonly reviewerRoles?: number;
    readonly rolesCount?: number;
};

export type JobArchitecture = {
    readonly id: string;
    readonly statistics: JobArchitectureStatistics;
    readonly title: string;
};

type JobArchitectureSelectProps = {
    readonly architectures: readonly JobArchitecture[];
    readonly createNewArchitecture?: string;
    readonly currentArchitecture?: JobArchitecture;
    readonly getStatsContent: (data: JobArchitectureStatistics | undefined) => ReactNode;
    readonly onCreateNewArchitecture?: () => void;
    readonly onOpenChange?: (open: boolean) => void;
    readonly onSwitchArchitecture: (id: string) => void;
    readonly open?: boolean;
    readonly placeholder?: string;
    readonly size?: TriggerSize;
    readonly triggerBackgroundColor?: string;
};

export const JobArchitectureSelect: FunctionComponent<JobArchitectureSelectProps> = ({
    architectures,
    createNewArchitecture = 'Create new architecture',
    currentArchitecture,
    getStatsContent,
    onCreateNewArchitecture = undefined,
    onOpenChange,
    onSwitchArchitecture,
    open,
    placeholder = 'Select Architecture',
    size = 'm',
    triggerBackgroundColor,
}) => {
    return (
        <Root
            open={open}
            value={currentArchitecture?.id}
            onOpenChange={onOpenChange}
            onValueChange={onSwitchArchitecture}
        >
            <Trigger
                className={clsx(
                    'flex flex-col shrink-0 border-none rounded-lg cursor-pointer font-sans outline-none w-full',
                    'transition-[background-color] duration-200 ease-in-out',
                    'focus-visible:outline focus-visible:outline-1 focus-visible:outline-orange-800',
                    size === 'l' ? 'gap-s py-l px-xxl' : 'py-s px-m',
                )}
                style={{
                    backgroundColor: triggerBackgroundColor ?? 'var(--color-white-200)',
                }}
                onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor =
                        triggerBackgroundColor ?? 'var(--color-gray-200)';
                }}
                onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor =
                        triggerBackgroundColor ?? 'var(--color-white-200)';
                }}
            >
                <div className="flex items-center gap-xs w-full">
                    <div className="text-black-700 text-sm font-medium text-left">
                        <Value placeholder={placeholder}>{currentArchitecture?.title ?? placeholder}</Value>
                    </div>
                    <div className="text-gray-700 h-[16px] w-[16px] transition-transform duration-200 ease-in-out">
                        <IconChevronGrabberVertical size={16} />
                    </div>
                </div>
                <div className="text-gray-700 text-xs font-medium text-left w-full flex items-center gap-1 flex-wrap">
                    {getStatsContent(currentArchitecture?.statistics)}
                </div>
            </Trigger>
            <Portal>
                <Content
                    className="bg-white-200 border border-gray-300 rounded-md shadow-md flex flex-col max-h-[50vh] overflow-hidden w-[25rem] z-[99999]"
                    position="popper"
                    sideOffset={4}
                >
                    {onCreateNewArchitecture !== undefined && (
                        // biome-ignore lint/a11y/useButtonType: suppressed due to migration
                        <button
                            className="appearance-none border-none bg-transparent flex items-center rounded-sm box-border text-gray-900 cursor-pointer shrink-0 text-sm font-medium gap-s justify-start py-xl px-m w-full hover:bg-gray-200 [&>span]:flex-1 font-sans text-left"
                            onClick={onCreateNewArchitecture}
                        >
                            <IconPlus size={16} />
                            <span>{createNewArchitecture}</span>
                        </button>
                    )}
                    <Viewport className="bg-white-200 flex-1 max-h-full min-h-0 overflow-y-auto p-xs">
                        {architectures.map((architecture) => (
                            <StyledItem
                                key={architecture.id}
                                style={{ flexDirection: 'column' }}
                                value={architecture.id}
                            >
                                <div className="flex items-start text-black-700 text-sm font-medium leading-[1.25rem] text-left">
                                    <ItemText className="flex-1">{architecture.title}</ItemText>
                                    <ItemIndicator className="inline-flex items-center justify-center text-gray-900 ml-s">
                                        <CheckIcon />
                                    </ItemIndicator>
                                </div>
                                <div className="text-gray-800 text-xxs font-normal leading-[1.25rem] flex items-center gap-1 flex-wrap">
                                    {getStatsContent(architecture.statistics)}
                                </div>
                            </StyledItem>
                        ))}
                    </Viewport>
                </Content>
            </Portal>
        </Root>
    );
};
